import gsap from "gsap";
import { useEffect, useMemo, useState } from "react";
import {
  consentCookieName,
  currencyCookieName,
  defaultPotName,
  selectedPotStorageKey,
  stateStorageKey
} from "../constants";
import {
  fetchSpendingPotTrackerState,
  fetchSpendingPots,
  postArchiveSpendingPot,
  postDefaultSpendingPot,
  postRestoreSpendingPot,
  postSpendingPot,
  postSpendingPotSpendEntry,
  postSpendingPotSpendEntryAction,
  postStartSpendingPotTracking
} from "../api/spendTrackApi";
import { dayNumberForDate, isAfterPeriodEnd, totalDaysForPeriod } from "../logic/dates";
import { findCurrencyOption, loadCurrencyPreference } from "../logic/preferences";
import { validatePotName } from "../logic/potNameRules";
import {
  getCookie,
  loadSetupDefaults,
  loadThemeMode,
  loadTrackerState,
  saveSetupDefaults,
  saveThemeMode,
  saveTrackerState,
  setCookie
} from "../logic/storage";
import type {
  CurrencyPreference,
  PeriodType,
  Screen,
  SetupDefaults,
  SpendingConfiguration,
  SpendingPot,
  SpendingPotTrackerApiResponse,
  ThemeMode
} from "../types";

export function useSpendTrackState({ isAuthenticated }: { isAuthenticated: boolean }) {
  const [screen, setScreen] = useState<Screen>("setup");
  const [configuration, setConfiguration] = useState<SpendingConfiguration | null>(null);
  const [currentDayNumber, setCurrentDayNumber] = useState(1);
  const [spendByDay, setSpendByDay] = useState<Record<string, number>>({});
  const [undoStackByDay, setUndoStackByDay] = useState<Record<string, number[]>>({});
  const [redoStackByDay, setRedoStackByDay] = useState<Record<string, number[]>>({});
  const [spendAmountText, setSpendAmountText] = useState("");
  const [storageConsent, setStorageConsent] = useState(() => getCookie(consentCookieName) === "accepted");
  const [currencyPreference, setCurrencyPreference] = useState(loadCurrencyPreference);
  const [setupDefaults, setSetupDefaults] = useState<SetupDefaults>(loadSetupDefaults);
  const [themeMode, setThemeMode] = useState<ThemeMode>(loadThemeMode);
  const [spendingPots, setSpendingPots] = useState<SpendingPot[]>([]);
  const [selectedPotId, setSelectedPotId] = useState(() => localStorage.getItem(selectedPotStorageKey) ?? "");
  const [potStatusMessage, setPotStatusMessage] = useState<string | null>(null);
  const [restoreErrorMessage, setRestoreErrorMessage] = useState<string | null>(null);
  const isServerTrackingMode = isAuthenticated && selectedPotId !== "";

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    const savedState = loadTrackerState();

    if (!savedState) {
      return;
    }

    setConfiguration(savedState.configuration);
    setSpendByDay(savedState.spendByDay);
    setUndoStackByDay(savedState.undoStackByDay ?? {});
    setRedoStackByDay(savedState.redoStackByDay ?? {});

    if (isAfterPeriodEnd(new Date(), savedState.configuration)) {
      setScreen("result");
      return;
    }

    setCurrentDayNumber(dayNumberForDate(new Date(), savedState.configuration));
    setScreen("dashboard");
  }, [isAuthenticated]);

  useEffect(() => {
    gsap.fromTo(".tracker-screen", { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.35 });
  }, [screen]);

  useEffect(() => {
    if (!configuration) {
      return;
    }

    if (isAuthenticated) {
      return;
    }

    saveTrackerState({
      configuration,
      currentDayNumber,
      spendByDay,
      undoStackByDay,
      redoStackByDay
    });
  }, [configuration, currentDayNumber, isAuthenticated, redoStackByDay, spendByDay, undoStackByDay]);

  useEffect(() => {
    saveThemeMode(themeMode);
  }, [themeMode]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void refreshSpendingPots();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isServerTrackingMode) {
      return;
    }

    void refreshServerTrackerState();
  }, [isServerTrackingMode, selectedPotId]);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(currencyPreference.locale, {
        style: "currency",
        currency: currencyPreference.currency,
        maximumFractionDigits: 2
      }),
    [currencyPreference]
  );
  const selectedCurrencyOption = findCurrencyOption(currencyPreference);
  const selectedPot = spendingPots.find((pot) => pot.id === selectedPotId) ?? null;
  const currentPotName = isAuthenticated ? selectedPot?.name ?? defaultPotName : null;

  function startTracking(periodAllowance: number, periodType: PeriodType) {
    if (isAuthenticated) {
      const nextSetupDefaults = { periodAllowance, periodType };
      setSetupDefaults(nextSetupDefaults);
      saveSetupDefaults(nextSetupDefaults);
      void startLoggedInTracking(periodAllowance, periodType);
      return;
    }

    const startDate = new Date();
    const newConfiguration: SpendingConfiguration = {
      periodAllowance,
      startDateIso: startDate.toISOString(),
      periodType,
      totalPeriodDays: totalDaysForPeriod(periodType, startDate)
    };
    const nextSetupDefaults = { periodAllowance, periodType };

    setConfiguration(newConfiguration);
    setSetupDefaults(nextSetupDefaults);
    setCurrentDayNumber(1);
    setSpendByDay({});
    setUndoStackByDay({});
    setRedoStackByDay({});
    setSpendAmountText("");
    setScreen("dashboard");
    saveSetupDefaults(nextSetupDefaults);
  }

  function resetTracking() {
    if (!isServerTrackingMode) {
      localStorage.removeItem(stateStorageKey);
    }

    setConfiguration(null);
    setCurrentDayNumber(1);
    setSpendByDay({});
    setUndoStackByDay({});
    setRedoStackByDay({});
    setSpendAmountText("");
    setScreen("setup");
  }

  function openSetup() {
    setSpendAmountText("");
    setScreen("setup");
  }

  function openPotSelector() {
    setSpendAmountText("");
    setPotStatusMessage(null);
    setScreen("potSelector");

    if (isAuthenticated) {
      void refreshSpendingPots();
    }
  }

  function returnToBudget() {
    setSpendAmountText("");
    setScreen("dashboard");
  }

  function updateCurrencyPreference(nextPreference: CurrencyPreference) {
    setCurrencyPreference(nextPreference);
    setCookie(currencyCookieName, JSON.stringify(nextPreference), 365);
  }

  function acceptStorageConsent() {
    setStorageConsent(true);
    setCookie(consentCookieName, "accepted", 365);
  }

  async function refreshSpendingPots() {
    try {
      const pots = await fetchSpendingPots();
      setSpendingPots(pots);

      if (selectedPotId && !pots.some((pot) => pot.id === selectedPotId)) {
        setSelectedPotId("");
        localStorage.removeItem(selectedPotStorageKey);
      }
    } catch {
      setPotStatusMessage("Could not load spending pots. Try again in a moment.");
    }
  }

  async function refreshServerTrackerState() {
    await refreshServerTrackerStateForPot(selectedPotId);
  }

  async function refreshServerTrackerStateForPot(potId: string) {
    try {
      const state = await fetchSpendingPotTrackerState(potId);
      applyServerTrackerState(state);
    } catch {
      setPotStatusMessage("Could not load tracker data for this pot.");
    }
  }

  async function startLoggedInTracking(periodAllowance: number, periodType: PeriodType) {
    try {
      const potId = await ensureSelectedPot();
      await startServerTrackingForPot(potId, periodAllowance, periodType);
    } catch {
      setPotStatusMessage("Could not prepare a spending pot for tracking.");
    }
  }

  async function startServerTrackingForPot(potId: string, periodAllowance: number, periodType: PeriodType) {
    try {
      const state = await postStartSpendingPotTracking(potId, {
        periodAllowance,
        periodType,
        startDateUtc: new Date().toISOString()
      });
      applyServerTrackerState(state);
    } catch {
      setPotStatusMessage("Could not start tracking for this pot.");
    }
  }

  async function ensureSelectedPot() {
    if (selectedPotId) {
      return selectedPotId;
    }

    const pots = spendingPots.length > 0 ? spendingPots : await fetchSpendingPots();
    if (pots.length > 0) {
      const defaultPot = pots.find((pot) => pot.name.toLocaleLowerCase() === defaultPotName.toLocaleLowerCase());
      const pot = defaultPot ?? pots[0];
      setSpendingPots(pots);
      setSelectedPotId(pot.id);
      localStorage.setItem(selectedPotStorageKey, pot.id);
      return pot.id;
    }

    const pot = await postDefaultSpendingPot(currencyPreference.currency);

    setSpendingPots([pot]);
    setSelectedPotId(pot.id);
    localStorage.setItem(selectedPotStorageKey, pot.id);
    setPotStatusMessage("Default pot created.");
    return pot.id;
  }

  async function addServerSpend(amount: number) {
    try {
      const state = await postSpendingPotSpendEntry(selectedPotId, {
        amount,
        dayNumber: currentDayNumber
      });
      applyServerTrackerState(state);
      setSpendAmountText("");
    } catch {
      setPotStatusMessage("Could not save spend for this pot.");
    }
  }

  async function undoServerSpend() {
    try {
      const state = await postSpendingPotSpendEntryAction(selectedPotId, "undo", currentDayNumber);
      applyServerTrackerState(state);
    } catch {
      setPotStatusMessage("Could not undo spend for this pot.");
    }
  }

  async function redoServerSpend() {
    try {
      const state = await postSpendingPotSpendEntryAction(selectedPotId, "redo", currentDayNumber);
      applyServerTrackerState(state);
    } catch {
      setPotStatusMessage("Could not redo spend for this pot.");
    }
  }

  function applyServerTrackerState(state: SpendingPotTrackerApiResponse) {
    if (!state.hasActivePeriod) {
      setConfiguration(null);
      setSpendByDay({});
      setUndoStackByDay({});
      setRedoStackByDay({});
      setCurrentDayNumber(1);
      setScreen("setup");
      return;
    }

    const nextConfiguration: SpendingConfiguration = {
      periodAllowance: Number(state.periodAllowance),
      periodType: state.periodType,
      startDateIso: state.startDateUtc,
      totalPeriodDays: state.totalPeriodDays
    };

    setConfiguration(nextConfiguration);
    setSpendByDay(normalizeNumberRecord(state.spendByDay));
    setUndoStackByDay(createPlaceholderStacks(state.undoCountByDay));
    setRedoStackByDay(createPlaceholderStacks(state.redoCountByDay));

    if (isAfterPeriodEnd(new Date(), nextConfiguration)) {
      setScreen("result");
      return;
    }

    setCurrentDayNumber(dayNumberForDate(new Date(), nextConfiguration));
    setScreen("dashboard");
  }

  async function createSpendingPot(name: string) {
    const validation = validatePotName(name, spendingPots);
    if (validation) {
      throw new Error(validation);
    }

    const pot = await postSpendingPot({
      currencyCode: currencyPreference.currency,
      name
    });
    const nextPots = [...spendingPots, pot].sort((left, right) => left.name.localeCompare(right.name));

    setSpendingPots(nextPots);
    void selectSpendingPot(pot.id);
    setPotStatusMessage("Pot created.");
  }

  async function archiveSpendingPot(potId: string) {
    if (potId === selectedPotId) {
      setPotStatusMessage("The selected pot cannot be archived.");
      return;
    }

    try {
      await postArchiveSpendingPot(potId);
      await refreshSpendingPots();
      setPotStatusMessage("Pot archived.");
    } catch {
      setPotStatusMessage("Could not archive pot.");
    }
  }

  async function restoreSpendingPot(potId: string) {
    try {
      const restoredPot = await postRestoreSpendingPot(potId);
      const nextPots = spendingPots
        .map((pot) => (pot.id === restoredPot.id ? restoredPot : pot))
        .sort((left, right) => Number(left.isArchived) - Number(right.isArchived) || left.name.localeCompare(right.name));
      setSpendingPots(nextPots);
      setRestoreErrorMessage(null);
      setPotStatusMessage("Pot restored.");
    } catch (error) {
      setRestoreErrorMessage(error instanceof Error ? error.message : "Could not restore pot.");
    }
  }

  async function selectSpendingPot(potId: string) {
    setSelectedPotId(potId);
    localStorage.setItem(selectedPotStorageKey, potId);
    setConfiguration(null);
    setSpendByDay({});
    setUndoStackByDay({});
    setRedoStackByDay({});
    setSpendAmountText("");
    setPotStatusMessage("Pot selected.");
    await refreshServerTrackerStateForPot(potId);
  }

  function toggleTheme() {
    setThemeMode((current) => (current === "light" ? "dark" : "light"));
  }

  return {
    screen,
    configuration,
    currentDayNumber,
    spendByDay,
    undoStackByDay,
    redoStackByDay,
    spendAmountText,
    storageConsent,
    currencyPreference,
    setupDefaults,
    themeMode,
    spendingPots,
    selectedPotId,
    potStatusMessage,
    restoreErrorMessage,
    isServerTrackingMode,
    formatter,
    selectedCurrencyOption,
    currentPotName,
    setScreen,
    setCurrentDayNumber,
    setSpendByDay,
    setUndoStackByDay,
    setRedoStackByDay,
    setSpendAmountText,
    startTracking,
    resetTracking,
    openSetup,
    openPotSelector,
    returnToBudget,
    updateCurrencyPreference,
    acceptStorageConsent,
    createSpendingPot,
    archiveSpendingPot,
    restoreSpendingPot,
    selectSpendingPot,
    addServerSpend,
    undoServerSpend,
    redoServerSpend,
    toggleTheme
  };
}

function normalizeNumberRecord(record: Record<string, number>) {
  return Object.fromEntries(Object.entries(record).map(([key, value]) => [key, Number(value)]));
}

function createPlaceholderStacks(countByDay: Record<string, number>) {
  return Object.fromEntries(
    Object.entries(countByDay).map(([dayNumber, count]) => [dayNumber, Array.from({ length: count }, () => 1)])
  );
}
